"use client";

import { useEffect, useState } from "react";
import { List, X } from "lucide-react";
import { cn } from "@/lib/utils";

type TocItem = {
  id: string;
  text: string;
  level: number;
};

export default function FloatingTOCPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");

  // ─── Collect headings ───
  useEffect(() => {
    const collect = () => {
      const headings = Array.from(
        document.querySelectorAll<HTMLHeadingElement>(
          "main h1, main h2, main h3"
        )
      );
      const items: TocItem[] = headings
        .filter((h) => h.textContent && h.offsetParent !== null)
        .map((h, idx) => {
          if (!h.id) {
            h.id = `toc-${idx}-${(h.textContent || "")
              .trim()
              .toLowerCase()
              .replace(/[^\p{L}\p{N}]+/gu, "-")
              .replace(/^-|-$/g, "")
              .slice(0, 50)}`;
          }
          return {
            id: h.id,
            text: (h.textContent || "").trim(),
            level: parseInt(h.tagName.substring(1), 10),
          };
        });
      setTocItems(items);
    };

    const t = setTimeout(collect, 150);
    return () => clearTimeout(t);
  }, []);

  // ─── Scroll spy ───
  useEffect(() => {
    if (tocItems.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 }
    );
    tocItems.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [tocItems]);

  // ─── Lock body scroll khi mở ───
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const offset = 80;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
    history.replaceState(null, "", `#${id}`);
    setIsOpen(false);
  };

  if (tocItems.length === 0) return null;

  return (
    <>
      {/* Floating button — ẩn trên xl vì đã có sidebar */}
      <button
        onClick={() => setIsOpen(true)}
        aria-label="Mở mục lục"
        className={cn(
          "xl:hidden fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-teal-900 hover:bg-teal-800 text-white shadow-card-lg flex items-center justify-center transition-all hover:scale-110",
          isOpen && "opacity-0 pointer-events-none"
        )}
      >
        <List className="w-5 h-5" />
      </button>

      {/* Overlay */}
      <div
        onClick={() => setIsOpen(false)}
        className={cn(
          "fixed inset-0 z-50 bg-teal-900/40 backdrop-blur-sm transition-opacity",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
      />

      {/* Panel */}
      <div
        className={cn(
          "fixed z-50 bg-card rounded-2xl shadow-card-lg transition-all",
          "bottom-4 right-4 left-4 max-h-[70vh]",
          "sm:bottom-6 sm:right-6 sm:left-auto sm:w-80",
          isOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-4 pointer-events-none"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-orange-600">
              Mục lục
            </div>
            <div className="font-display font-bold text-teal-900 text-sm mt-0.5">
              Trang Giới thiệu
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            aria-label="Đóng"
            className="w-8 h-8 rounded-lg hover:bg-teal-50 flex items-center justify-center text-foreground/60 hover:text-teal-900 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* List */}
        <nav className="overflow-y-auto max-h-[55vh] px-3 py-3">
          <ul className="space-y-0.5">
            {tocItems.map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  onClick={(e) => handleClick(e, item.id)}
                  className={cn(
                    "block py-2 px-3 rounded-lg text-sm transition-all",
                    item.level === 2 && "pl-3",
                    item.level === 3 && "pl-7",
                    activeId === item.id
                      ? "bg-orange-50 text-orange-600 font-bold"
                      : "text-foreground/70 hover:bg-teal-50 hover:text-teal-900"
                  )}
                >
                  {item.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </>
  );
}
